import React, { useEffect } from 'react';
import { useRouter } from 'next/router';

import { useUpdateEffect } from '@tager/web-core';

import {
  convertedSelectedFilters,
  getCarBadges,
  getCarImages,
  getCarLink,
  getCarPrice,
  getCarStatus,
  getCarTags,
  getSelectedFilterFromUrl,
  getSEOType,
  handleSetSelectedFiltersFromUrl,
  setActiveBrandsAndModelsFromSettings,
  setSelectedFiltersQuery,
  isCarComparing,
} from '@/modules/CarsStock/components/common.helpers';
import {
  CarsListItem,
  CarsListWrapper,
} from '@/modules/CarsStock/components/common.styles';
import StockCarCard from '@/components/StockCarCard';
import StockCarCardSkeleton from '@/components/StockCarCardSkeleton';
import { useTypedDispatch, useTypedSelector } from '@/store/store';
import {
  getComparisonCars,
  getCurrentSort,
  getPage,
  getSelectedFilters,
  getStockBrandsAndModels,
  getStockBrandsAndModelsThunk,
  getStockCarList,
  getStockCarListStatus,
  getStockCarListThunk,
  getStockFilterParams,
  getStockFilterParamsStatus,
  getStockFilterParamsThunk,
  setComparisonCar,
} from '@/store/reducers/cars-stock';
import useSettingItem from '@/hooks/useSettingItem';
import { useStockCarsSelectOptions } from '@/modules/CarsStock/components/common.hooks';
import { fetchStockCarsBannerThunk } from '@/store/reducers/banners';
import { fetchSeoTemplateThunk } from '@/store/reducers/seo-template';
import { usePhoneNumber } from '@/hooks/usePhoneNumber';
import { skeletonCardsLimit } from '@/modules/CarsStock/components/common.constants';

import CarSelectionHelper from './components/CarSelectionHelper';
import Banner from './components/Banner';
import { CarsListProps } from './types';

function CarsList({ carsType }: CarsListProps) {
  const router = useRouter();
  const dispatch = useTypedDispatch();
  const brandsAndModels = useTypedSelector(getStockBrandsAndModels);
  const filterParamsStatus = useTypedSelector(getStockFilterParamsStatus);
  const filterParams = useTypedSelector(getStockFilterParams);
  const carsListStatus = useTypedSelector(getStockCarListStatus);
  const carsListData = useTypedSelector(getStockCarList);
  const comparisonCars = useTypedSelector(getComparisonCars);
  const page = useTypedSelector(getPage);
  const sort = useTypedSelector(getCurrentSort);
  const sortOptions = useStockCarsSelectOptions(carsType);
  const selectedFilters = useTypedSelector(getSelectedFilters);
  const activeCarsBrands = useSettingItem('STOCK_NEW_BRANDS');
  const activeCarsModels = useSettingItem('STOCK_NEW_MODELS');
  const phone = usePhoneNumber();

  // initial effect
  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    if (carsListStatus === 'IDLE') {
      try {
        (async () => {
          const selectedItems = getSelectedFilterFromUrl(router.query.slug);

          const [brandsAndModels, filterParams] = await Promise.all([
            dispatch(getStockBrandsAndModelsThunk(carsType)),
            dispatch(getStockFilterParamsThunk(carsType)),
          ]);

          if (carsType === 'new') {
            setActiveBrandsAndModelsFromSettings(
              dispatch,
              activeCarsBrands,
              activeCarsModels,
              brandsAndModels
            );
          }

          let bannerThunk = null;

          if (router.query && router.query.slug) {
            const selectedItems = getSelectedFilterFromUrl(router.query.slug);
            const convertedFilters = convertedSelectedFilters(
              selectedItems,
              brandsAndModels,
              filterParams
            );

            handleSetSelectedFiltersFromUrl(dispatch, convertedFilters);

            if (
              convertedFilters.brand.length === 1 &&
              convertedFilters.model.length === 1 &&
              convertedFilters.page === 1
            ) {
              const { brand, model } = convertedFilters;

              bannerThunk = fetchStockCarsBannerThunk({
                type: carsType,
                brand: brand[0].value,
                model: model[0].value,
              });
            } else if (
              convertedFilters.brand.length === 1 &&
              convertedFilters.model.length === 0 &&
              convertedFilters.page === 1
            ) {
              const { brand } = convertedFilters;

              bannerThunk = fetchStockCarsBannerThunk({
                type: carsType,
                brand: brand[0].value,
              });
            }
          } else {
            bannerThunk = fetchStockCarsBannerThunk({
              type: carsType,
            });
          }

          const seoType = getSEOType(carsType, selectedItems);

          await Promise.all([
            bannerThunk && dispatch(bannerThunk),
            dispatch(
              getStockCarListThunk(carsType, undefined, undefined, signal)
            ),
            dispatch(fetchSeoTemplateThunk(seoType)),
          ]);
        })();
      } catch (error) {
        console.log('error = ', error);
      }
    }

    return () => {
      abortController.abort();
    };
  }, [router.query]);

  // effect when update stock filters
  useUpdateEffect(() => {
    if (carsListStatus === 'IDLE' || carsListStatus === 'LOADING') {
      return;
    }

    try {
      (async () => {
        const slug = setSelectedFiltersQuery(
          selectedFilters,
          brandsAndModels,
          filterParams,
          page.currentPage,
          { item: sort, data: sortOptions }
        );

        const selectedItems = getSelectedFilterFromUrl([slug]);
        const seoType = getSEOType(carsType, selectedItems);

        let bannerThunk = null;

        if (
          selectedFilters.selectedCars.selectedBrands.length === 0 &&
          selectedFilters.selectedCars.selectedModels.length === 0 &&
          page.currentPage === 1
        ) {
          bannerThunk = fetchStockCarsBannerThunk({
            type: carsType,
          });
        } else if (
          selectedFilters.selectedCars.selectedBrands.length === 1 &&
          selectedFilters.selectedCars.selectedModels.length === 1 &&
          page.currentPage === 1
        ) {
          const { selectedBrands, selectedModels } =
            selectedFilters.selectedCars;

          bannerThunk = fetchStockCarsBannerThunk({
            type: carsType,
            brand: selectedBrands[0].value,
            model: selectedModels[0].value,
          });
        } else if (
          selectedFilters.selectedCars.selectedBrands.length === 1 &&
          selectedFilters.selectedCars.selectedModels.length === 0 &&
          page.currentPage === 1
        ) {
          const { selectedBrands } = selectedFilters.selectedCars;

          bannerThunk = fetchStockCarsBannerThunk({
            type: carsType,
            brand: selectedBrands[0].value,
          });
        }

        await Promise.all([
          bannerThunk && dispatch(bannerThunk),
          dispatch(getStockCarListThunk(carsType)),
          dispatch(fetchSeoTemplateThunk(seoType)),
        ]);
      })();
    } catch (error) {
      console.log('error = ', error);
    }
  }, [selectedFilters, page, sort]);

  // update filters params, when update car models filters
  useUpdateEffect(() => {
    if (filterParamsStatus === 'IDLE' || filterParamsStatus === 'LOADING') {
      return;
    }

    try {
      (async () => {
        await dispatch(getStockFilterParamsThunk(carsType));
      })();
    } catch (error) {
      console.log('error = ', error);
    }
  }, [
    selectedFilters.selectedCars.selectedBrands,
    selectedFilters.selectedCars.selectedModels,
    selectedFilters.selectedCars.selectedComplectations,
  ]);

  return (
    <CarsListWrapper>
      {carsListStatus !== 'SUCCESS' ? (
        new Array(skeletonCardsLimit).fill(null).map((_, index) => (
          <CarsListItem key={index} view="portrait">
            <StockCarCardSkeleton view="portrait" />
          </CarsListItem>
        ))
      ) : carsListData &&
        carsListData.items &&
        carsListData.items.length !== 0 ? (
        <React.Fragment>
          {carsListData.items.map((car, index) => (
            <React.Fragment key={car.id}>
              {index === 4 && <CarSelectionHelper />}

              <Banner position={index} />

              <CarsListItem view="portrait">
                <StockCarCard
                  images={getCarImages(car)}
                  link={getCarLink(car.type, car.id)}
                  name={car.name}
                  subName={car.complectation ?? ''}
                  price={getCarPrice(car, phone)}
                  totalImagesCount={car.imagesCount}
                  tags={getCarTags(car)}
                  badgesList={getCarBadges(car)}
                  approved={car.approved}
                  grade={car.grade ?? undefined}
                  view="portrait"
                  isCompared={isCarComparing(comparisonCars, car.id)}
                  onCompareChange={() =>
                    dispatch(
                      setComparisonCar({ carType: car.type, id: car.id })
                    )
                  }
                  carStatus={getCarStatus(car)}
                  stockStatus={car.stockStatus ?? undefined}
                  isMobile
                />
              </CarsListItem>
            </React.Fragment>
          ))}

          {carsListData.meta.countAll - page.offset <= 4 && (
            <CarSelectionHelper />
          )}
        </React.Fragment>
      ) : (
        <React.Fragment>
          {carsListData?.lastAvailableCar && (
            <CarsListItem view="portrait">
              <StockCarCard
                images={getCarImages(carsListData.lastAvailableCar)}
                link={carsListData.lastAvailableCar?.catalogUrl ?? ''}
                name={carsListData.lastAvailableCar.name}
                subName={carsListData.lastAvailableCar.complectation ?? ''}
                price={getCarPrice(carsListData.lastAvailableCar, phone)}
                totalImagesCount={carsListData.lastAvailableCar.imagesCount}
                tags={getCarTags(carsListData.lastAvailableCar)}
                view="portrait"
                carStatus="Под заказ"
                isMobile
              />
            </CarsListItem>
          )}

          <CarSelectionHelper />
        </React.Fragment>
      )}
    </CarsListWrapper>
  );
}

export default CarsList;
